import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  markConversationRead,
  type Conversation,
  type PagedConversations,
} from '@/features/conversations/conversations.api';
import { conversationsKeys } from '@/features/conversations/keys';

interface ReadVariables {
  conversationId: string;
}

interface InfiniteSnapshot {
  queryKey: readonly unknown[];
  data: { pages: PagedConversations[]; pageParams: unknown[] } | undefined;
}

interface ReadMutationContext {
  /** Snapshots of every `['conversations', 'list', ...]` cache slot we
   *  patched, so onError can roll them all back. We snapshot the whole
   *  page array because there are typically only one or two filter
   *  variants live at any time. */
  snapshots: InfiniteSnapshot[];
}

/**
 * Mark a conversation as read.
 *
 * The unread badge lives inside the list response (`conversation.unread_count`)
 * — so the optimistic patch must walk every cached infinite list and zero
 * out the matching row. The manual `marked_unread` flag is cleared in the
 * same patch: the backend's read endpoint resets both in one UPDATE (any
 * agent reading clears the shared-desk attention flag), so the optimistic
 * row must agree or the attention dot would linger until the refetch. On
 * error we restore every snapshot.
 */
export function useReadConversation() {
  const queryClient = useQueryClient();

  return useMutation<Conversation, unknown, ReadVariables, ReadMutationContext>({
    mutationFn: ({ conversationId }) => markConversationRead(conversationId),

    onMutate: async ({ conversationId }) => {
      // Cancel any in-flight list refetches so the optimistic patch isn't
      // immediately overwritten by stale server data.
      await queryClient.cancelQueries({ queryKey: conversationsKeys.lists() });

      // Find every cached list and snapshot its current shape. We can't use
      // `setQueriesData` because the data shape is the infinite-query
      // envelope `{ pages, pageParams }`, not a flat list.
      const snapshots: InfiniteSnapshot[] = queryClient
        .getQueriesData<{ pages: PagedConversations[]; pageParams: unknown[] }>({
          queryKey: conversationsKeys.lists(),
        })
        .map(([queryKey, data]) => ({ queryKey, data }));

      for (const { queryKey, data } of snapshots) {
        if (!data) continue;
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((c) =>
              c.id === conversationId
                ? { ...c, unread_count: 0, marked_unread: false }
                : c,
            ),
          })),
        });
      }

      return { snapshots };
    },

    onSuccess: (data) => {
      // Seed the detail slot with the POST's returned ConversationOut
      // (previously discarded). ThreadPanel resolves merged-thread members
      // from the `detail(id)` slots that otherwise only the WS projection
      // writes — during a WS outage the `view=unread_pin` refetch can
      // legitimately EMPTY every list cache (a fully-read thread leaves the
      // view), and the thread would lose member resolution (the composer
      // degrades). The read response carries the full row; writing it here
      // keeps member resolution alive without depending on the WS echo.
      queryClient.setQueryData(conversationsKeys.detail(data.id), data);
    },

    onError: (_error, _vars, context) => {
      if (!context) return;
      for (const { queryKey, data } of context.snapshots) {
        queryClient.setQueryData(queryKey, data);
      }
      // Quiet: marking-read failures are low-stakes (the next poll fixes it).
      toast("Couldn't mark as read");
    },

    onSettled: () => {
      // Server's count wins eventually.
      void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
    },
  });
}
